import { CouponState, CouponType, ExchangeState } from '@tencent/wphk-act-base-types';
import { jumpPage } from '@tencent/wphk-components-jump-page';
import { ItemInfo, FixedCouponType, ConfigCouponInfo } from '../types';
import { i18n } from '../i18n';
import { CouponButtonType, ThemeColor } from '@tencent/wphk-act-base-ui';
import { getServerTime } from '@tencent/wphk-components-common/utils/server-time';
import dayjs from 'dayjs';

/**
 * 是否为电子现金券
 */
function isAutoCoupon(couponType: CouponType): boolean {
  return [CouponType.FIXED, CouponType.DISC, CouponType.FLOATING].includes(couponType);
}
/**
 * 去使用券
 */
export function go2UsedCoupon(couponType: CouponType, userCouponid: string) {
  // 去使用，分展示券 vs 电子现金券
  if (isAutoCoupon(couponType)) {
    // 电子现金券
    jumpPage.goCouponDetail({
      userScid: userCouponid,
      adtag: 'scangift',
    });
  } else {
    // 展示券
    jumpPage.goMerchantCouponDetail({
      userScid: userCouponid,
      adtag: 'scangift',
    });
  }
}

// 券类型优先级
const fixedCouponType2weight = {
  [FixedCouponType.merchant]: 100,
  [FixedCouponType.invitedUser]: 90,
  [FixedCouponType.common]: 80,
  [FixedCouponType.easterEgg]: 70,
  default: 110, // 如果没有定义类型，说明是新增的，将其放在最前面
};
// 状态排序优先级
const couponState2weight = {
  [CouponState.received]: 100,
  [CouponState.notReceived]: 90,
  [CouponState.used]: 80,
  [CouponState.expired]: 60,
};
const exchangeState2weight = {
  [ExchangeState.dayLimit]: 70, // 日限额排在过期之前
};
/**
 * 排序，先聚合（券类型优先级），然后状态（状态排序优先级），最后金额大小（discount)
 */
export function sortCoupon(couponArr: ItemInfo[], scanGiftCouponInfo: ConfigCouponInfo[]): ItemInfo[] {
  const tmpArr: ItemInfo[] = [...couponArr];
  // 状态排序
  tmpArr.sort((a, b) => {
    const scanGiftA = scanGiftCouponInfo.find(it => it.actIdEn === a.getCouponParams.enActid);
    const scanGiftB = scanGiftCouponInfo.find(it => it.actIdEn === b.getCouponParams.enActid);
    const scanGiftAweight = fixedCouponType2weight[scanGiftA?.type ?? 'default'] ?? fixedCouponType2weight.default;
    const scanGiftBweight = fixedCouponType2weight[scanGiftB?.type ?? 'default'] ?? fixedCouponType2weight.default;

    // 不同类型
    if (scanGiftAweight !== scanGiftBweight) {
      return scanGiftBweight - scanGiftAweight; // 倒叙
    }

    // 同类型，不同状态
    if (a.couponState !== b.couponState) {
      const wA = couponState2weight[a.couponState] || (a.exchangeState && exchangeState2weight[a.exchangeState]) || 0;
      const wB = couponState2weight[b.couponState] || (b.exchangeState && exchangeState2weight[b.exchangeState]) || 0;
      return wB - wA;  // 不同状态，倒叙
    }
    if (a.discount === b.discount) {
      return a.requiredNum - b.requiredNum; // 金额相同，门槛金额升序
    }

    // 同类型，同状态
    return a.discount - b.discount; // 同状态，金额升序
  });

  return tmpArr;
}

/**
 * 是否需要将btn展示出来，只有“去使用”和“领取”需要展示
 */
export function checkIsNeedShowBtn(couponState?: CouponState): boolean {
  if (undefined === couponState) return false;
  return [
    CouponState.notReceived,
    CouponState.received,
    // CouponState.used,
    // CouponState.expired,
  ].includes(couponState);
}

/**
 * 是否为日限额
 * @param exchangeState
 */
export function isDayLimit(exchangeState?: ExchangeState): boolean {
  return undefined !== exchangeState && exchangeState === ExchangeState.dayLimit;
}
/**
 * 是否为月限额
 */
function isMonthLimit(exchangeState?: ExchangeState): boolean {
  return undefined !== exchangeState && exchangeState === ExchangeState.monthUserLimit;
}

/**
 * 获取券面的lock/disabled状态（通过buttonType控制）
 * @param exchangeState
 */
export function getButtonTypeForSmallCoupon(item: ItemInfo, ableTimeList: string[][]): {
  buttonType?: CouponButtonType;
} {
  if (checkIfLockCoupon(item.exchangeState) || checkIfInAbleStateButDisableTime(ableTimeList, item.couponState)) {
    return {
      buttonType: CouponButtonType.lock,  // 半透明，不可点击
    };
  }
  if (checkIfDiabledCoupon(item.couponState)) {
    return {
      buttonType: CouponButtonType.disabled,  // 灰色底，不可点击
    };
  }
  return {};
}

/** 自定义券面主题色 */
enum SelfDefinedThemeColor {
  /** 默认的是绿色 */
  // default = 'green',
  /** 金色 */
  golden = 'self_golden',
  // TODO: 如果有新增主题色，需要到../less/skin.less种增加相应的样式
};
/** 券类型 对应的 券面主题色 */
const fixedCouponType2ThemeColor = {
  // [FixedCouponType.easterEgg]: SelfDefinedThemeColor.golden,
  [FixedCouponType.invitedUser]: SelfDefinedThemeColor.golden,
  [FixedCouponType.merchant]: SelfDefinedThemeColor.golden,
};
/** 券类型 对应的 tag */
const fixedCouponType2TagText = {
  [FixedCouponType.easterEgg]: i18n('惊喜彩蛋'),
  [FixedCouponType.invitedUser]: i18n('特邀用户'),
};
/**
 * 根据券类型，获取券面的主题色
 */
export function getThemeAndTagByFixedCouponType(fixedCouponType: FixedCouponType) {
  return {
    themeColor: fixedCouponType2ThemeColor[fixedCouponType] || ThemeColor.green,
    couponTagText: fixedCouponType2TagText[fixedCouponType],
  };
}

/**
 * 日/月限额时，将券面锁住
 */
function checkIfLockCoupon(exchangeState?: ExchangeState): boolean {
  return isDayLimit(exchangeState) || isMonthLimit(exchangeState);
}

/**
 * 已使用/已过期时，将券面灰掉
 */
function checkIfDiabledCoupon(couponState?: CouponState): boolean {
  return undefined !== couponState
    && [CouponState.used, CouponState.expired].includes(couponState);
}

/**
 * 判断是否处于"可领取状态"下的"不可领时间"
 */
export function checkIfInAbleStateButDisableTime(ableTimeList: string[][], couponState: CouponState): boolean {
  if (couponState !== CouponState.notReceived) {
    // 如果是非可领取状态，直接返回false，无须判断下面的时间
    return false;
  }
  // 处于可领券状态时，判断配置的可领券时间
  let isDisableTime = true;
  const now = dayjs(getServerTime()).toDate();
  const len = ableTimeList.length;
  for (let i = 0; i < len; i++) {
    // [startTime, endTime]
    if (now > dayjs(ableTimeList[i][0]).toDate() && now < dayjs(ableTimeList[i][1]).toDate()) {
      isDisableTime = false;
      break;
    }
  }

  return isDisableTime;
}

const exchangeState2tip = {
  [ExchangeState.dayLimit]: i18n('今日已領完'),
  [ExchangeState.monthUserLimit]: i18n('本月已領完'),
  [ExchangeState.totalLimit]: i18n('已領完'),
};
/**
 * 限额提示文案(日限额/月限额)
 */
export function getLimitTip(exchangeState?: ExchangeState): string {
  if (undefined === exchangeState) return '';
  return exchangeState2tip[exchangeState] ?? '';
}
